import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { useMediaQuery } from "react-responsive";
import { Header } from "../../components/shared/Header";
import { NoData } from "../../components/shared/NoData";
import { Select } from "../../components/shared/Select";
import { Option } from "../../models/Option";
import { Button } from "../../components/shared/Button";
import { getPrivateDiplomas, getPrivateDiplomaSearchOptions } from "../../services/DiplomaService";
import { SelectLoader } from "../../components/loaders/SelectLoader";
import { DiplomaSearchCriteria } from "../../models/DiplomaSearchCriteria";
import { DiplomaModel } from "../../models/DiplomaModel";
import { TableLoader } from "../../components/loaders/TableLoader";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";

export const PrivateDiplomaListPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();     
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("diplomaListPage"));  
    const authContext = useContext(AuthContext);    
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();  
    const categoryId: number | undefined = searchParams.get("category-id") === null ? undefined : +searchParams.get("category-id")!;
    const instituteId: number | undefined = searchParams.get("institute-id") === null ? undefined : +searchParams.get("institute-id")!;
    const periodId: number | undefined = searchParams.get("period-id") === null ? undefined : +searchParams.get("period-id")!;
    const keyword: string | undefined = searchParams.get("keyword") === null ? undefined : searchParams.get("keyword")!;                 
    const [categories, setCategories] = useState<Option[]>([]);
    const [institutes, setInstitutes] = useState<Option[]>([]);
    const [periods, setPeriods] = useState<Option[]>([]);
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [diplomas, setDiplomas] = useState<DiplomaModel[]>([]);    
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();   
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [loading, setLoading] = useState<boolean>(true); 
    const [optionLoading, setOptionLoading] = useState<boolean>(true);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();

    useEffect(() => {
        if(admin === null) {
            toAdminLogin("Please login to access this page.");
        }
        else {
            getSearchOptions();
        }
    }, []);
    
    useEffect(() => {
        getDiplomaList();                                    
    }, [categoryId, instituteId, periodId, keyword]);

    useEffect(() => {        
        setStyles(styleContext.getComponentStyle("diplomaListPage")); 
    }, [isMobile]);        

    async function getSearchOptions() {                
        await getPrivateDiplomaSearchOptions()
        .then((options: any) => {
            setCategories(options.categories);
            setInstitutes(options.privateInstitutes);
            setPeriods(options.diplomaPeriods);
            setOptionLoading(false);            
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }
    
    async function getDiplomaList() {                
        setLoading(true);   
        let searchCriteria: DiplomaSearchCriteria = {
            pageNo: 1,
            recordsPerPage: 500,
            instituteId: instituteId,
            diplomaPeriodId: periodId,
            categoryId: categoryId,
            keyword: keyword,
            languageCode: "SIN"
        }      
        await getPrivateDiplomas(searchCriteria)
        .then((diplomaList: DiplomaModel[]) => {
            setDiplomas(diplomaList);  
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    const setCategory = (selectedCategoryId: string) => {  
        if(selectedCategoryId) {
            searchParams.set("category-id", selectedCategoryId);
        }
        else {
            searchParams.delete("category-id");
        }
        handleNavigate();
    }

    const setInstitute = (selectedInstituteId: string) => {         
        if(selectedInstituteId) {        
            searchParams.set("institute-id", selectedInstituteId);
        }
        else {
            searchParams.delete("institute-id");
        }
        handleNavigate();
    }
    
    const setPeriod = (selectedPeriodId: string) => {  
        if(selectedPeriodId) {
            searchParams.set("period-id", selectedPeriodId);
        }
        else {
            searchParams.delete("period-id");
        }
        handleNavigate();
    }

    const setKeyword = (keywordInput: string) => {
        if(keywordInput === "") {
            searchParams.delete("keyword");
        }   
        else {                
            searchParams.set("keyword", keywordInput);
        }
        handleNavigate();
    }
    
    const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();  
        const value = event.target.value;
        setSearchKeyword(value);

        // Clear previous timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // Set new timeout
        const newTimeoutId = setTimeout(() => {
            setKeyword(value.trim());
        }, 1000);
        setTimeoutId(newTimeoutId);
    };

    const handleNavigate = () => {                                    
        const newUrl = `/private-diplomas?${searchParams}`;
        navigate(newUrl);                
    }

    const navigateToStateDiplomas = () => {
        navigate("/state-diplomas");
    }

    const navigateToEdit = (diplomaId: number) => {
        navigate("/private-diploma/edit/" + diplomaId);
    }

    return (   
        <>
            <Header /> 
            <div style={styles}>                         
                <div style={styles.accordionButtonsContainer}>
                    <div 
                        style={{ ...styles.accordionButton, ...styles.accordionButton.left }}
                        onClick={navigateToStateDiplomas}     
                    >
                        {"State Diplomas"}                        
                    </div>
                    <div style={{ ...styles.accordionButton, ...styles.accordionButton.right, ...styles.accordionButton.selected }}>
                        {"Private Diplomas"}
                    </div>
                </div>
                {optionLoading ? <SelectLoader count={4} />  :      
                <div style={styles.searchWrapper}>
                    {categories.length > 0 && <Select 
                        styles={styles.searchSelect}
                        label={"Categories"} 
                        options={categories} 
                        initialValue={categoryId?.toString()}
                        getSelectedValue={setCategory} 
                    />}
                    {institutes.length > 0 && <Select 
                        styles={styles.searchSelect}
                        label={"Institutes"} 
                        options={institutes} 
                        initialValue={instituteId?.toString()}
                        getSelectedValue={setInstitute} 
                    />}
                    {periods.length > 0 && <Select 
                        styles={styles.searchSelect}
                        label={"Periods"} 
                        options={periods} 
                        initialValue={periodId?.toString()}
                        searchDisabled={true}
                        getSelectedValue={setPeriod} 
                    />}                                        
                    <input
                        type={"text"}
                        placeholder="Search by keywords"
                        value={searchKeyword}
                        onChange={(e) => handleKeywordChange(e)} 
                        style={styles.searchInput}                                                                                    
                    />
                </div>}
                {loading ? <TableLoader columnCount={5} rowCount={10} /> : 
                <div>                
                    {(diplomas.length === 0) ? <NoData/> :
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.table.header}> 
                                <th style={styles.table.idColumn}>{"Id"}</th>                           
                                <th style={styles.table.instituteColumn}>{"Institute"}</th>
                                <th style={styles.table.diplomaColumn}>{"Diploma"}</th>
                                <th style={styles.table.periodColumn}>{"Period"}</th>                              
                            </tr>
                        </thead>
                        <tbody>
                            {diplomas.map((diploma, index) => (
                                <tr 
                                    key={index} 
                                    style={{...styles.table.tr, ...(index === hoveredIndex && styles.table.hoveredTr)}}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(-1)}
                                >
                                    <td>{diploma.diplomaId}</td>
                                    <td>
                                        <div style={styles.table.rowWrapper}>
                                            <div>
                                                <img src={diploma.instituteLogoUrl} style={styles.table.logo}></img>
                                            </div>
                                            <div>
                                                {diploma.instituteName}<br/>
                                                {diploma.instituteName_ENG}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {diploma.diplomaName}<br/>
                                        {diploma.diplomaName_ENG}                                        
                                    </td>
                                    <td style={styles.table.periodCell}>
                                        <div>{diploma.diplomaPeriod}</div>
                                        {index === hoveredIndex && <Button style={styles.table.editButton} onClick={() => navigateToEdit(diploma.diplomaId)}>Edit</Button>}
                                    </td>                                    
                                </tr>
                            ))}
                        </tbody>
                    </table>}                    
                </div>}
            </div>             
        </>         
    )
}